import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { useT } from "@/context/LanguageContext";

const containerV = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.55 } },
};
const lineV = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] } },
};
const fadeUp = {
  hidden: { y: 20, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero({ hero }) {
  const ref = useRef(null);
  const [showVeil, setShowVeil] = useState(true);
  const { t, ui } = useT();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 160]);
  const yLogo = useTransform(scrollY, [0, 800], [0, -40]);
  const scaleBg = useTransform(scrollY, [0, 800], [1.02, 1.12]);

  useEffect(() => {
    const t = setTimeout(() => setShowVeil(false), 1000);
    return () => clearTimeout(t);
  }, []);

  const sparkles = Array.from({ length: 14 }, (_, i) => ({
    id: i,
    top: `${Math.random() * 90 + 3}%`,
    left: `${Math.random() * 96 + 2}%`,
    size: 6 + Math.random() * 10,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 3,
  }));

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden with-grain"
      data-testid="hero-section"
    >
      <motion.div
        initial={{ y: 0 }}
        animate={{ y: showVeil ? 0 : "-100%" }}
        transition={{ duration: 1.1, ease: [0.85, 0, 0.15, 1], delay: showVeil ? 0 : 0.05 }}
        className="loader-veil"
      >
        <div className="flex flex-col items-center gap-4">
          <img src={hero.logo_url} alt="Logo" className="h-16 w-16 object-contain" />
          <span className="overline text-[color:var(--gcs-gold)]">{ui("loader.brand")}</span>
        </div>
      </motion.div>

      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 z-0 will-change-transform">
        <img src={hero.background_image} alt="Impeccable space" className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-[color:var(--gcs-emerald)]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--gcs-emerald)]/60 via-transparent to-[color:var(--gcs-emerald)]/85" />
      </motion.div>

      {sparkles.map((s) => (
        <div
          key={s.id}
          className="sparkle absolute pointer-events-none z-10"
          style={{ top: s.top, left: s.left, animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s` }}
        >
          <svg width={s.size} height={s.size} viewBox="0 0 24 24" fill="none">
            <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill="var(--gcs-gold)" />
          </svg>
        </div>
      ))}

      <div className="absolute top-24 md:top-28 left-6 md:left-12 z-20 text-[color:var(--gcs-ivory)]">
        <div className="flex items-center gap-2">
          <span className="h-px w-8 bg-[color:var(--gcs-gold)]" />
          <span className="overline text-[color:var(--gcs-gold)]">{ui("hero.badge_area")}</span>
        </div>
      </div>
      <div className="absolute top-24 md:top-28 right-6 md:right-12 z-20 text-right text-[color:var(--gcs-ivory)]">
        <span className="overline text-[color:var(--gcs-gold)]">{ui("hero.chapter")}</span>
      </div>

      <div className="relative z-20 mx-auto max-w-7xl px-6 md:px-12 pt-40 md:pt-48 pb-24 md:pb-32 min-h-[100svh] flex flex-col justify-end">
        <motion.div variants={containerV} initial="hidden" animate={showVeil ? "hidden" : "show"}>
          <motion.p
            variants={fadeUp}
            className="text-[color:var(--gcs-gold)] font-sans text-xs md:text-sm uppercase tracking-[0.32em] mb-6 md:mb-8"
            data-testid="hero-eyebrow"
          >
            {t(hero.eyebrow)}
          </motion.p>

          <h1 className="font-serif text-[color:var(--gcs-ivory)] text-[52px] leading-[0.95] sm:text-[76px] md:text-[110px] lg:text-[132px] tracking-[-0.02em]">
            <span className="line-mask" data-testid="hero-line-1">
              <motion.span variants={lineV} className="inline-block">{t(hero.line_1)}</motion.span>
            </span>
            <span className="line-mask" data-testid="hero-line-2">
              <motion.span variants={lineV} className="inline-block italic text-[color:var(--gcs-gold)]">{t(hero.line_2)}</motion.span>
            </span>
            <span className="line-mask" data-testid="hero-line-3">
              <motion.span variants={lineV} className="inline-block">{t(hero.line_3)}</motion.span>
            </span>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              variants={fadeUp}
              className="md:col-span-7 text-[color:var(--gcs-ivory)]/80 text-base md:text-lg max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t(hero.subtitle)}
            </motion.p>

            <motion.div variants={fadeUp} className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
              <a
                href="#contacto"
                className="inline-flex items-center gap-2 rounded-full btn-gold px-6 py-3 text-sm font-semibold"
                data-testid="hero-cta-primary"
              >
                <Sparkles className="w-4 h-4" />
                {ui("cta.book")}
              </a>
              <a
                href="#precios"
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gcs-ivory)]/50 text-[color:var(--gcs-ivory)] px-6 py-3 text-sm font-semibold hover:bg-[color:var(--gcs-ivory)] hover:text-[color:var(--gcs-emerald)] transition-colors"
                data-testid="hero-cta-secondary"
              >
                {ui("cta.view_pricing")}
                <ArrowDownRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          <motion.div
            variants={fadeUp}
            style={{ y: yLogo }}
            className="absolute right-6 md:right-12 top-40 md:top-44 hidden lg:block"
            data-testid="hero-dynamic-logo"
          >
            <div className="relative flex items-center gap-4 rounded-2xl bg-[color:var(--gcs-ivory)]/95 backdrop-blur-sm border border-[color:var(--gcs-gold)]/40 pl-4 pr-6 py-3 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.4)]">
              <img
                src={hero.logo_url}
                alt="General Cleaning Services logo"
                className="h-16 w-16 md:h-20 md:w-20 object-contain"
              />
              <div className="flex flex-col leading-tight">
                <span className="font-serif italic text-sm text-[color:var(--gcs-gold)]">{ui("hero.owner_by")}</span>
                <span className="font-serif text-lg md:text-xl text-[color:var(--gcs-emerald)]">General Cleaning</span>
                <span className="font-serif text-sm text-[color:var(--gcs-emerald)]/70">Services, LLC</span>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-12 flex items-center gap-3 text-[color:var(--gcs-ivory)]/60">
            <span className="h-px w-10 bg-[color:var(--gcs-ivory)]/60" />
            <span className="overline text-[color:var(--gcs-ivory)]/70">{ui("cta.scroll")}</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
